// Prevent redefinition if the script is loaded multiple times
if (typeof window.CartCountdown === 'undefined') {
  window.CartCountdown = class extends HTMLElement {
    static timerInterval = null;
    static instances = new Set();
    static DURATION = 13 * 60; // 13 minutes in seconds
    static STORAGE_KEY = 'cartCountdown';
    static ATTRIBUTE_KEY = 'countdown_end';
    static lastUpdateTime = 0;
    static rafId = null;
    static sharedObserver = null;
    static updateQueue = new Set();
    static isUpdating = false;
    static stringCache = new Map();
    static isInitializing = false; // Prevent concurrent initialization

    // Performance monitoring
    static perf = {
      marks: new Map(),
      measure(name, start, end) {
        if (performance.mark && performance.measure) {
          try {
            // If start and end are numbers (timestamps), use them directly
            if (typeof start === 'number' && typeof end === 'number') {
              performance.measure(name, { start, end });
            } else {
              // If they are mark names, use them as is
              performance.measure(name, start, end);
            }
          } catch (e) {
            // Ignore performance API errors
            console.warn('Performance API error:', e);
          }
        }
      }
    };

    constructor() {
      super();
      this.selector = this.getAttribute('selector') || null;
      this.display = null;
      this.lastDisplayValue = null;
      this.isVisible = true;
      this.isDestroyed = false;
      this.cachedDisplay = null;

      // Bind methods for better performance
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      try {
        const startTime = performance.now();

        // Cache DOM query
        this.cachedDisplay = this.selector
          ? document.querySelector(this.selector)
          : this.querySelector('.cart-countdown-display') || this;

        this.display = this.cachedDisplay;

        CartCountdown.instances.add(this);

        // Use shared observer for better memory efficiency
        this.setupSharedVisibilityObserver();

        // Prevent race condition: only ONE instance should initialize
        if (!CartCountdown.timerInterval && !CartCountdown.isInitializing) {
          this.startCountdown();
        } else {
          // If timer is already running or initializing, just update this instance's display
          const endTime = localStorage.getItem(CartCountdown.STORAGE_KEY);
          if (endTime) {
            const currentTime = Math.floor(Date.now() / 1000);
            const timeLeft = Math.max(0, parseInt(endTime, 10) - currentTime);
            this.updateDisplay(timeLeft);
          }
        }

        // Measure performance properly
        const endTime = performance.now();
        CartCountdown.perf.measure('countdown-init', startTime, endTime);
      } catch (error) {
        console.warn('CartCountdown initialization error:', error);
      }
    }

    disconnectedCallback() {
      this.isDestroyed = true;
      CartCountdown.instances.delete(this);
      CartCountdown.updateQueue.delete(this);

      if (CartCountdown.sharedObserver) {
        CartCountdown.sharedObserver.unobserve(this);
      }

      if (CartCountdown.instances.size === 0) {
        this.cleanup();
      }
    }

    cleanup() {
      clearInterval(CartCountdown.timerInterval);
      CartCountdown.timerInterval = null;
      CartCountdown.isInitializing = false;

      if (CartCountdown.rafId) {
        cancelAnimationFrame(CartCountdown.rafId);
        CartCountdown.rafId = null;
      }

      if (CartCountdown.sharedObserver) {
        CartCountdown.sharedObserver.disconnect();
        CartCountdown.sharedObserver = null;
      }

      // Clear caches
      CartCountdown.stringCache.clear();
      CartCountdown.updateQueue.clear();
      CartCountdown.isUpdating = false;
    }

    setupSharedVisibilityObserver() {
      if (!('IntersectionObserver' in window)) return;

      if (!CartCountdown.sharedObserver) {
        CartCountdown.sharedObserver = new IntersectionObserver((entries) => {
          // Use requestIdleCallback for non-critical updates
          const processEntries = () => {
            entries.forEach(entry => {
              if (entry.target instanceof CartCountdown) {
                entry.target.handleVisibilityChange(entry.isIntersecting);
              }
            });
          };

          if (window.requestIdleCallback) {
            requestIdleCallback(processEntries, { timeout: 100 });
          } else {
            setTimeout(processEntries, 0);
          }
        }, {
          threshold: 0.1,
          rootMargin: '50px' // Preload slightly before visible
        });
      }

      CartCountdown.sharedObserver.observe(this);
    }

    handleVisibilityChange(isVisible) {
      if (this.isDestroyed) return;

      const wasVisible = this.isVisible;
      this.isVisible = isVisible;

      // Only trigger update if visibility changed and now visible
      if (!wasVisible && isVisible) {
        CartCountdown.updateQueue.add(this);
        this.scheduleUpdate();
      } else if (!isVisible) {
        CartCountdown.updateQueue.delete(this);
      }
    }

    // Optimized string formatting with caching
    formatTime(timeLeft) {
      if (CartCountdown.stringCache.has(timeLeft)) {
        return CartCountdown.stringCache.get(timeLeft);
      }

      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;

      // Micro-optimization: use template literal for better performance
      const formatted = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

      // Cache with LRU-like behavior (limit cache size)
      if (CartCountdown.stringCache.size > 100) {
        const firstKey = CartCountdown.stringCache.keys().next().value;
        CartCountdown.stringCache.delete(firstKey);
      }

      CartCountdown.stringCache.set(timeLeft, formatted);
      return formatted;
    }

    updateDisplay(timeLeft) {
      if (this.isDestroyed || !this.isVisible || !this.display) return;

      const formatted = this.formatTime(timeLeft);

      // Debounced DOM update - only update when display value changes
      if (this.lastDisplayValue !== formatted) {
        this.lastDisplayValue = formatted;

        // Use textContent for better performance than innerHTML
        this.display.textContent = formatted;

        // Dispatch custom event for other components (optional)
        this.dispatchEvent(new CustomEvent('countdown-update', {
          detail: { timeLeft, formatted },
          bubbles: false // Don't bubble for performance
        }));
      }
    }

    scheduleUpdate() {
      if (CartCountdown.isUpdating || CartCountdown.updateQueue.size === 0) return;

      CartCountdown.isUpdating = true;

      if (CartCountdown.rafId) {
        cancelAnimationFrame(CartCountdown.rafId);
      }

      CartCountdown.rafId = requestAnimationFrame(() => {
        this.processBatchUpdate();
        CartCountdown.isUpdating = false;
      });
    }

    processBatchUpdate() {
      if (CartCountdown.updateQueue.size === 0) return;

      const endTime = localStorage.getItem(CartCountdown.STORAGE_KEY);
      if (!endTime) return;

      const currentTime = Math.floor(Date.now() / 1000);
      const timeLeft = Math.max(0, parseInt(endTime, 10) - currentTime);

      // Batch process all visible instances
      CartCountdown.updateQueue.forEach(instance => {
        if (!instance.isDestroyed && instance.isVisible) {
          instance.updateDisplay(timeLeft);
        }
      });

      CartCountdown.updateQueue.clear();
    }

    async startCountdown() {
      // Set flag immediately to prevent race condition
      CartCountdown.isInitializing = true;

      try {
        const now = Math.floor(Date.now() / 1000);
        // Try to get a shared timestamp from cart attributes first
        let endTime = await this.fetchCartAttributeEndTime().catch(() => null);
        if (!endTime) {
          endTime = localStorage.getItem(CartCountdown.STORAGE_KEY);
        }

        if (!endTime || parseInt(endTime, 10) <= now) {
          endTime = now + CartCountdown.DURATION;
          try {
            localStorage.setItem(CartCountdown.STORAGE_KEY, endTime.toString());
          } catch (e) {
            // Fallback if localStorage is not available
            console.warn('localStorage not available, countdown will reset on page refresh');
          }
          // Persist to cart attribute so Checkout UI can read it
          try {
            await this.setCartAttributeEndTime(endTime);
          } catch (e) {
            // Non-fatal
            console.warn('Failed to set cart attribute for countdown:', e);
          }
        }

        const updateTimer = async () => {
          try {
            const currentTime = Math.floor(Date.now() / 1000);
            const timeLeft = Math.max(0, parseInt(endTime, 10) - currentTime);

            // Yield to prevent blocking main thread
            if (CartCountdown.instances.size > 5) { // Only yield if many instances
              await this.yieldToMain();
            }

            // Add all visible instances to update queue
            CartCountdown.instances.forEach(instance => {
              if (instance.isVisible && !instance.isDestroyed) {
                CartCountdown.updateQueue.add(instance);
              }
            });

            // Schedule batch update
            this.scheduleUpdate();

            if (timeLeft <= 0) {
              this.handleCountdownComplete();
              // Clear the shared attribute when finished
              try {
                await this.clearCartAttributeEndTime();
              } catch (e) {
                // ignore
              }
            }
          } catch (error) {
            console.warn('Timer update error:', error);
          }
        };

        // Initial update
        await updateTimer();

        // Use setInterval with error handling
        CartCountdown.timerInterval = setInterval(updateTimer, 1000);
      } finally {
        // Always unset the flag when initialization completes
        CartCountdown.isInitializing = false;
      }
    }

    handleCountdownComplete() {
      clearInterval(CartCountdown.timerInterval);
      CartCountdown.timerInterval = null;

      if (CartCountdown.rafId) {
        cancelAnimationFrame(CartCountdown.rafId);
        CartCountdown.rafId = null;
      }

      // Final update to show 00:00
      CartCountdown.instances.forEach(instance => {
        if (!instance.isDestroyed) {
          instance.updateDisplay(0);
        }
      });

      try {
        localStorage.removeItem(CartCountdown.STORAGE_KEY);
      } catch (e) {
        // Ignore localStorage errors
      }

      // Dispatch completion event
      document.dispatchEvent(new CustomEvent('cart-countdown-complete', {
        detail: { instances: CartCountdown.instances.size }
      }));
    }

    // Optimized yield function with multiple strategies
    async yieldToMain() {
      return new Promise(resolve => {
        if (typeof scheduler !== 'undefined' && scheduler.postTask) {
          scheduler.postTask(resolve, { priority: 'background' });
        } else if (window.requestIdleCallback) {
          requestIdleCallback(resolve, { timeout: 50 });
        } else {
          setTimeout(resolve, 0);
        }
      });
    }

    // ===== Shared timestamp via Cart Attribute =====
    async fetchCartAttributeEndTime() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const cart = await res.json();
        const val = cart && cart.attributes ? cart.attributes[CartCountdown.ATTRIBUTE_KEY] : null;
        const num = val != null ? parseInt(val, 10) : NaN;
        return Number.isFinite(num) ? num : null;
      } catch (e) {
        return null;
      }
    }

    async setCartAttributeEndTime(endTime) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes: { [CartCountdown.ATTRIBUTE_KEY]: endTime.toString() }
          })
        });
      } catch (e) {
        // swallow
      }
    }

    async clearCartAttributeEndTime() {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes: { [CartCountdown.ATTRIBUTE_KEY]: '' }
          })
        });
      } catch (e) {
        // swallow
      }
    }
  }

  // Only define the custom element if it hasn't been defined yet
  if (!customElements.get('cart-countdown')) {
    customElements.define('cart-countdown', window.CartCountdown);
  }
}
